import { groupBy } from 'lodash';

import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { AiSuggestionKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { CommonAiSuggestionFragment } from '../graphql/aiSuggestions.generated';
import {
  FetchTestamentaryEntityAiSuggestionsV2Query,
  useFetchTestamentaryEntityAiSuggestionsV2Query,
} from '../graphql/testamentaryEntitySuggestionsV2.generated';

interface UseFetchTestamentaryEntityAISuggestionsOptions {
  testamentaryEntityId: string | undefined | null;
  skip?: boolean;
  onCompleted?: (data: FetchTestamentaryEntityAiSuggestionsV2Query) => void;
}

export interface ProcessedTestamentaryEntitySuggestionsData {
  suggestions: CommonAiSuggestionFragment[];
  groupedByKind: Record<AiSuggestionKind, CommonAiSuggestionFragment[]>;
}

/**
 * Hook for fetching testamentary entity AI suggestions.
 *
 * This hook is gated by the `aikg_v2_associations_poc` feature flag.
 * When the flag is disabled, the query is skipped and empty results are returned.
 *
 * Testamentary entity suggestions always use KGV2 (no V1 fallback needed).
 */
export function useFetchTestamentaryEntityAISuggestions({
  testamentaryEntityId,
  skip,
  onCompleted,
}: UseFetchTestamentaryEntityAISuggestionsOptions) {
  const subtrustAISuggestionsEnabled = useFeatureFlag(
    'aikg_v2_associations_poc'
  );

  const shouldSkip =
    skip || !testamentaryEntityId || !subtrustAISuggestionsEnabled;

  const result = useFetchTestamentaryEntityAiSuggestionsV2Query({
    skip: shouldSkip,
    variables: {
      testamentaryEntityID: testamentaryEntityId!,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted,
  });

  return {
    ...result,
    subtrustAISuggestionsEnabled,
  };
}

/**
 * Processes testamentary entity suggestions query data,
 * extracting suggestions and grouping them by kind.
 */
export function processTestamentaryEntitySuggestionsData(
  data: FetchTestamentaryEntityAiSuggestionsV2Query
): ProcessedTestamentaryEntitySuggestionsData {
  const suggestions = getNodes(
    data.aiSuggestions
  ) as CommonAiSuggestionFragment[];

  const groupedByKind = groupBy(suggestions, (s) => s.kind) as Record<
    AiSuggestionKind,
    CommonAiSuggestionFragment[]
  >;

  return { suggestions, groupedByKind };
}
